import { ProgramInfo } from "../../types/program.response";
import { Requirement } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import {
    getCompletedUnitsCount,
    getRequiredUnitsCount,
    getUnitsLabel,
    getOptionProgress,
    hasUnitsRequirement,
    isOptionLikeRequirement,
} from "../../utils/requirementResponseUtils";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import SidebarCompletedCourses from "../../components/requirement-detail/SidebarCompletedCourses";

interface RequirementInfoSidebarProps {
    program: ProgramInfo;
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    /** 切片未到时完成课程区域渲染骨架(进度条数据来自 skeleton,始终可用) */
    isSliceLoaded: boolean;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void | Promise<void>;
    handleDeleteCourse: (deletedCourse: CourseLite) => void | Promise<void>;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
}

export default function RequirementInfoSidebar({ program, requirement, coursesMap, isSliceLoaded, handleAddCourse, handleDeleteCourse, handleOverride }: RequirementInfoSidebarProps) {
    const completedCount = getCompletedUnitsCount(requirement);
    const requiredCount = getRequiredUnitsCount(requirement);
    const unitsLabel = getUnitsLabel(requirement);
    const progressPercentage = requiredCount > 0
        ? Math.min(100, Math.round((completedCount / requiredCount) * 100))
        : 0;

    const handleRequirementCourseAdd = async (course: AddableCourse, nodeId: string | null) => {
        if (nodeId && course.userItemId) {
            await handleOverride(course, {
                requirementId: requirement.info.id,
                nodeId,
                overrideType: "FORCE_APPLY",
            });
            return;
        }

        await handleAddCourse(
            course,
            nodeId ? [{ requirementId: requirement.info.id, nodeId, overrideType: "FORCE_APPLY" }] : [],
            null,
        );
    };

    return (
        <div className="bg-gray-50 rounded-md p-3">
            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Requirement
                        </div>
                        <h2 className="mt-1 text-sm font-bold text-gray-900">{requirement.info.name}</h2>
                    </div>
                </div>

                {isOptionLikeRequirement(requirement) ? (
                    <div className="mt-4 flex flex-col gap-3">
                        {(() => {
                            const { fulfilled, required } = getOptionProgress(requirement);
                            const pct = required > 0 ? Math.min(100, Math.round((fulfilled / required) * 100)) : 0;
                            return (
                                <div>
                                    <div className="flex items-baseline justify-between">
                                        <div className="text-xs font-medium text-gray-500">
                                            {fulfilled} / {required} options satisfied
                                        </div>
                                        <div className="text-xs font-medium text-gray-400">{pct}%</div>
                                    </div>
                                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
                                        <div
                                            className="h-full rounded-full bg-blue-500 transition-[width] duration-300 ease-out"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })()}
                        {hasUnitsRequirement(requirement) && (
                            <div>
                                <div className="flex items-baseline justify-between">
                                    <div className="text-xs font-medium text-gray-500">
                                        {completedCount} / {requiredCount} {unitsLabel}
                                    </div>
                                    <div className="text-xs font-medium text-gray-400">{progressPercentage}%</div>
                                </div>
                                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
                                    <div
                                        className="h-full rounded-full bg-blue-500 transition-[width] duration-300 ease-out"
                                        style={{ width: `${progressPercentage}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="mt-4">
                        <div className="flex items-baseline justify-between">
                            <div className="text-xs font-medium text-gray-500">
                                {completedCount} / {requiredCount} {unitsLabel}
                            </div>
                            <div className="text-xs font-medium text-gray-400">{progressPercentage}%</div>
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-blue-500 transition-[width] duration-300 ease-out"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 py-3">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Program</h2>
                <div className="mt-2 text-sm text-gray-800">{program.name}</div>
            </div>

            <div className="py-3">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Completed Courses</h2>
                {isSliceLoaded ? (
                    <SidebarCompletedCourses
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={handleRequirementCourseAdd}
                        onDelete={handleDeleteCourse}
                        onOverride={handleOverride}
                    />
                ) : (
                    <div className="mt-2 flex flex-col gap-1.5 animate-pulse">
                        <div className="h-7 rounded-md bg-gray-200" />
                        <div className="h-7 rounded-md bg-gray-200" />
                        <div className="h-7 rounded-md bg-gray-100" />
                    </div>
                )}
            </div>
            <p className="mt-1 pt-3 border-t border-gray-200 text-xs text-gray-500 leading-snug">
                For planning only — this does not guarantee that you've met all requirements. Always verify with official sources.
            </p>
            <p className="mt-1 pt-2 text-xs text-gray-500 leading-snug">
                Found an issue?{' '}
                <a href="https://forms.gle/QjjAxdeXQms7rNCF8" target="_blank" rel="noopener noreferrer" className="underline text-blue-400 hover:text-blue-600">Report here</a>
            </p>
        </div>
    );
}
